<template>
  <div class="pui-json-editor"></div>
</template>
<script>
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/closebrackets';
import { defaultKeymap, defaultTabBinding } from '@codemirror/commands';
import { commentKeymap } from '@codemirror/comment';
import { foldGutter, foldKeymap } from '@codemirror/fold';
import { highlightActiveLineGutter, lineNumbers } from '@codemirror/gutter';
import { defaultHighlightStyle } from '@codemirror/highlight';
import { history, historyKeymap } from '@codemirror/history';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { indentOnInput } from '@codemirror/language';
import { linter, lintKeymap } from '@codemirror/lint';
import { bracketMatching } from '@codemirror/matchbrackets';
import { rectangularSelection } from '@codemirror/rectangular-selection';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import {
  drawSelection,
  EditorView,
  highlightActiveLine,
  highlightSpecialChars,
  keymap,
} from '@codemirror/view';

const basicSetup = [
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  defaultHighlightStyle.fallback,
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  rectangularSelection(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...commentKeymap,
    ...completionKeymap,
    ...lintKeymap,
    defaultTabBinding,
  ]),
  EditorView.baseTheme({
    '&': { height: '100%', maxHeight: '100%' },
    '.cm-scroller': { overflow: 'auto' },
  }),
];

export default {
  props: {
    placeholder: {
      type: String,
      default: 'Insérez un JSON',
    },
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      code: this.value,
      view: null,
    };
  },
  watch: {
    value(newVal) {
      if (newVal === this.code) {
        return;
      }

      this.code = newVal;
      if (this.view) {
        // Replace everything with the new value
        const transaction = this.view.state.update({
          changes: { from: 0, to: this.view.state.doc.length, insert: this.code },
        });
        this.view.dispatch(transaction);
      }
    },
  },
  methods: {
    onCmChanges() {
      this.$emit('input', this.code);
    },
    refresh() {
      // This.$refs.myCm.refresh();
    },
    /**
     * @param {ViewUpdate} - editor view update
     */
    onUpdate(update) {
      if (update.docChanged) {
        const value = update.state.doc.toString();
        if (value !== this.code) {
          this.code = value;
          this.$emit('input', value);
        }
      }
    },
    // EXPOSED
    /**
     * @returns {SelectionRange[]} all selection ranges
     */
    getSelectionRanges() {
      if (!this.view) {
        return null;
      }
      return this.view.state.selection.ranges;
    },
    /**
     * @param {string} text - the text to insert
     * @param {number} from - where to start insert
     * @param {number} to - where to end insert
     * @returns {boolean} if the operation succeeded
     */
    insert(text, from, to) {
      if (!this.view) {
        return false;
      }
      const transaction = this.view.state.update({
        changes: { from, to, insert: text },
      });
      this.view.dispatch(transaction);
      return true;
    },
  },
  mounted() {
    const myJsonLinter = jsonParseLinter();
    this.view = new EditorView({
      state: EditorState.create({
        doc: this.value,
        extensions: [
          ...basicSetup,
          json(),
          linter(myJsonLinter, {}),
          EditorView.updateListener.of(this.onUpdate),
        ],
      }),
      parent: this.$el,
    });
  },
  beforeDestroy() {
    if (this.view) {
      this.view.destroy();
    }
  },
};
</script>
